import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { PaginatedResults, toCamelCase } from '../lib/typeHelper'
import { InsuranceApplicationSource } from './insuranceApplication'
import { Vendor } from './insuranceTypes'
import type { EmailMessage } from './lossRunRequest'

/** How far a carrier got on an application: the furthest along any of its offers got. */
export enum CarrierStatus {
  PREPARING = 'PREPARING',
  APPLICATION_SENT = 'APPLICATION_SENT',
  PRICED = 'PRICED',
  REQUEST_TO_BIND = 'REQUEST_TO_BIND',
  BOUND = 'BOUND',
  DECLINED = 'DECLINED',
  UNAVAILABLE = 'UNAVAILABLE',
  ERROR = 'ERROR',
}

export const carrierStatusLabels: Record<CarrierStatus, string> = {
  [CarrierStatus.PREPARING]: 'Preparing',
  [CarrierStatus.APPLICATION_SENT]: 'Application sent',
  [CarrierStatus.PRICED]: 'Priced',
  [CarrierStatus.REQUEST_TO_BIND]: 'Request to bind',
  [CarrierStatus.BOUND]: 'Bound',
  [CarrierStatus.DECLINED]: 'Declined',
  [CarrierStatus.UNAVAILABLE]: 'Unavailable',
  [CarrierStatus.ERROR]: 'Error',
}

export interface ApplicationCarrier {
  vendor: Vendor
  status: CarrierStatus
}

export interface InternalApplication {
  id: string
  name: string
  dateCreated: string
  source: InsuranceApplicationSource
  orgId: string
  orgName: string
  dotNumber: string | null
  carriers: ApplicationCarrier[]
  hasProspects: boolean
}

export interface Prospect {
  id: string
  dateCreated: string
  email: string | null
  mobile: string | null
}

interface UseInternalApplicationsParams {
  page?: number
  search?: string
  vendors?: Vendor[]
  carrierStatus?: CarrierStatus
  has?: string
  source?: string
  salesOwner?: string
}

function getInternalApplications(
  params: UseInternalApplicationsParams
): Promise<PaginatedResults<InternalApplication>> {
  const queryParams: Record<string, string> = {}
  if (params.page && params.page > 1) queryParams.page = String(params.page)
  if (params.search) queryParams.search = params.search
  if (params.vendors?.length) queryParams.vendors = params.vendors.join(',')
  if (params.carrierStatus) queryParams.carrier_status = params.carrierStatus
  if (params.has) queryParams.has = params.has
  if (params.source) queryParams.source = params.source
  if (params.salesOwner) queryParams.sales_owner = params.salesOwner

  return axios
    .get('/api/v1/insure/internal/applications/', { params: queryParams })
    .then((response) => toCamelCase(response.data) as PaginatedResults<InternalApplication>)
}

export function useInternalApplications(params: UseInternalApplicationsParams = {}) {
  const { data, isLoading } = useQuery({
    queryKey: [
      CacheKeys.INTERNAL_APPLICATIONS,
      params.page,
      params.search,
      params.vendors,
      params.carrierStatus,
      params.has,
      params.source,
      params.salesOwner,
    ],
    queryFn: () => getInternalApplications(params),
  })
  return {
    applications: data?.results ?? [],
    count: data?.count ?? 0,
    hasNextPage: !!data?.next,
    hasPreviousPage: !!data?.previous,
    isLoading,
  }
}

function getInternalApplication(id: string): Promise<InternalApplication> {
  return axios
    .get(`/api/v1/insure/internal/applications/${id}/`)
    .then((response) => toCamelCase(response.data) as InternalApplication)
}

export function useInternalApplication(id: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.INTERNAL_APPLICATIONS, id],
    queryFn: () => getInternalApplication(id!),
    enabled: !!id,
  })
  return { application: data, isLoading }
}

function getApplicationEmails(id: string): Promise<EmailMessage[]> {
  return axios
    .get(`/api/v1/insure/internal/applications/${id}/emails/`)
    .then((response) => toCamelCase(response.data) as EmailMessage[])
}

export function useApplicationEmails(id: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.INTERNAL_APPLICATIONS, id, 'emails'],
    queryFn: () => getApplicationEmails(id!),
    enabled: !!id,
    refetchInterval: 1000,
  })
  return { emails: data ?? [], isLoading }
}

function getApplicationProspects(id: string): Promise<Prospect[]> {
  return axios
    .get(`/api/v1/insure/internal/applications/${id}/prospects/`)
    .then((response) => toCamelCase(response.data) as Prospect[])
}

export function useApplicationProspects(id: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.INTERNAL_APPLICATIONS, id, 'prospects'],
    queryFn: () => getApplicationProspects(id!),
    enabled: !!id,
  })
  return { prospects: data ?? [], isLoading }
}

export enum SmsDirection {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
}

export enum SmsStatus {
  RECEIVED = 'RECEIVED',
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  FAILED = 'FAILED',
}

export interface SmsMessage {
  id: string
  dateCreated: string
  direction: SmsDirection
  fromPhone: string
  toPhone: string
  bodyText: string
  status: SmsStatus
  agentRunId: string | null
}

function getApplicationSmsMessages(id: string): Promise<SmsMessage[]> {
  return axios
    .get(`/api/v1/insure/internal/applications/${id}/sms-messages/`)
    .then((response) => toCamelCase(response.data) as SmsMessage[])
}

export function useApplicationSmsMessages(id: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.INTERNAL_APPLICATIONS, id, 'sms'],
    queryFn: () => getApplicationSmsMessages(id!),
    enabled: !!id,
  })
  return { messages: data ?? [], isLoading }
}

function deleteInternalApplication(id: string): Promise<void> {
  return axios.delete(`/api/v1/insure/internal/applications/${id}/`).then(() => undefined)
}

export function useDeleteInternalApplication() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteInternalApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.INTERNAL_APPLICATIONS] })
    },
  })
}
